import {
	ChangeDetectionStrategy,
	Component,
	computed,
	ContentChild,
	ContentChildren,
	ElementRef,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	output,
	OutputEmitterRef,
	QueryList,
	Signal,
	signal,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation,
	WritableSignal,
} from '@angular/core'
import { TableSettingsInterface } from '@shared/components/common/table/core/interfaces/table-settings.interface'
import { SortDirType } from '@shared/types/sort-dir.type'
import { ColumnInterface } from '@shared/interfaces/column.interface'
import { TranslatePipe } from '@ngx-translate/core'
import { TuiIcon, TuiScrollable, TuiScrollbar, tuiScrollbarOptionsProvider } from '@taiga-ui/core'
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common'
import { TuiSkeleton } from '@taiga-ui/kit'
import { EmptyTableComponent } from '@shared/components/common/empty/empty-table.component'
import { fromEvent, Subject, takeUntil } from 'rxjs'
import { TableActionsComponent } from '@shared/components/common/table/core/components/table-actions/table-actions.component'
import { TableFooterComponent } from '@shared/components/common/table/core/components/table-footer/table-footer.component'
import { TableDynamicRowComponent } from '@shared/components/common/table/core/components/table-dynamic-row/table-dynamic-row.component'
import { TableColumnComponent } from '@shared/components/common/table/core/components/table-column/table-column.component'
import { RowInterface } from '@shared/components/common/table/core/interfaces/row.interface'
import { ColumnSettingsInterface } from '@shared/components/common/table/core/interfaces/column-settings.interface'
import { ColumnSortInterface } from '@shared/components/common/table/core/interfaces/column-sort.interface'

@Component({
	selector: 'app-table',
	templateUrl: './table.component.html',
	styleUrl: './table.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [tuiScrollbarOptionsProvider({ mode: 'hover' })],
	encapsulation: ViewEncapsulation.None,
	imports: [
		TranslatePipe,
		TuiIcon,
		NgTemplateOutlet,
		TuiSkeleton,
		NgClass,
		EmptyTableComponent,
		NgStyle,
		TuiScrollbar,
		TuiScrollable,
	],
})
export class TableComponent<Item, Column> implements OnChanges, OnDestroy {
	protected readonly math: Math = Math
	protected readonly array: ArrayConstructor = Array
	protected readonly skeletonRowsSecondary: Array<number> = new this.array<number>(3)
	protected readonly skeletonRowsMain: Array<number> = new this.array<number>(10)
	protected readonly defaultTableSettings: TableSettingsInterface = {
		size: 'md',
		spreadContent: 'space-between',
		rowBorder: true,
		zebraRows: false,
		rowClickable: false,
		emptyTextPath: 'table.empty',
	}

	public statesColumns: InputSignal<ColumnInterface<Column>[]> = input<ColumnInterface<Column>[]>([])
	public settings: InputSignal<TableSettingsInterface | null> = input<TableSettingsInterface | null>(null)

	public items: InputSignal<Item[]> = input<Item[]>([])

	public isLoading: InputSignal<boolean> = input<boolean>(false)
	public isLoadingTop: InputSignal<boolean> = input<boolean>(false)
	public isLoadingBottom: InputSignal<boolean> = input<boolean>(false)

	public scrollToPosition: InputSignal<number> = input<number>(0) // Позиция куда нужно будет проскролить внутри таблицы по оси Y
	public isPoolScroll: InputSignal<boolean> = input<boolean>(false)

	public rowClick: OutputEmitterRef<Item> = output<Item>()
	public onReachTop: OutputEmitterRef<void> = output<void>()
	public onReachBottom: OutputEmitterRef<void> = output<void>()
	public onChangeSort: OutputEmitterRef<ColumnSortInterface<keyof Item>> = output<ColumnSortInterface<keyof Item>>()

	@ViewChild('tableScrollbar', {
		static: true,
		read: ElementRef,
	})
	tableScrollbar: ElementRef<HTMLElement> | null = null
	@ContentChild(TableActionsComponent) sourceActionComponent: TableActionsComponent | null = null
	@ContentChild(TableFooterComponent) sourceFooterComponent: TableFooterComponent | null = null
	@ContentChildren(TableColumnComponent) sourceColumnsComponents: QueryList<TableColumnComponent<Item, Column>> =
		new QueryList<TableColumnComponent<Item, Column>>()
	@ContentChildren(TableDynamicRowComponent) sourceDynamicRowsComponents: QueryList<TableDynamicRowComponent> =
		new QueryList<TableDynamicRowComponent>()
	private _destroy$: Subject<void> = new Subject<void>()

	private _isScrollListening: boolean = false
	private _resizeObserver: ResizeObserver | null = null
	private _activeSortDir: SortDirType = 'asc'
	private _hasRenderedItems: boolean = false

	protected isFirstLoad: WritableSignal<boolean> = signal<boolean>(true)

	protected hoveredRowIndex: WritableSignal<number | null> = signal<number | null>(null)
	protected propForSort: WritableSignal<keyof Item | null> = signal<keyof Item | null>(null)
	protected bodyWidth: WritableSignal<number> = signal<number>(0)
	protected actionsPosition: WritableSignal<number> = signal<number>(0)
	protected bodyHeight: WritableSignal<number> = signal<number>(0)

	protected resultItems: WritableSignal<Item[]> = signal<Item[]>(this.items())

	protected resultColumns: Signal<TableColumnComponent<Item, Column>[]> = computed<
		TableColumnComponent<Item, Column>[]
	>((): TableColumnComponent<Item, Column>[] => {
		const tableColumns: TableColumnComponent<Item, Column>[] = this.sourceColumnsComponents.toArray()
		const statesColumns: ColumnInterface<Column>[] = this.statesColumns()

		return statesColumns.reduce(
			(
				acc: TableColumnComponent<Item, Column>[],
				column: ColumnInterface<Column>,
			): TableColumnComponent<Item, Column>[] => {
				const foundColumn: TableColumnComponent<Item, Column> | undefined = tableColumns.find(
					(col: TableColumnComponent<Item, Column>): boolean =>
						column.column === col.resultSettings()?.column,
				)

				return foundColumn ? [...acc, foundColumn] : acc
			},
			[],
		)
	})

	protected minTableWidth: Signal<number> = computed<number>((): number => {
		const resultColumns: TableColumnComponent<Item, Column>[] = this.resultColumns()
		const statesColumns: ColumnInterface<unknown>[] = this.statesColumns()

		return resultColumns.reduce((acc: number, column: TableColumnComponent<Item, Column>): number => {
			const columnSettings: ColumnSettingsInterface<Item, Column> | null = column.resultSettings()
			if (!columnSettings) return acc

			const foundedColumn: ColumnInterface<unknown> | undefined = statesColumns.find(
				(col: ColumnInterface<unknown>): boolean => col.column === columnSettings.column,
			)

			return foundedColumn?.visible ? acc + (columnSettings.minWidthPx ?? 0) : acc
		}, 0)
	})
	protected resultTableSettings: Signal<TableSettingsInterface> = computed<TableSettingsInterface>(
		(): TableSettingsInterface => {
			return {
				...this.defaultTableSettings,
				...this.settings(),
			}
		},
	)

	protected hasVisibleColumns: Signal<boolean> = computed((): boolean => {
		const statesColumns: ColumnInterface<unknown>[] = this.statesColumns()
		return statesColumns.some((column: ColumnInterface<unknown>): boolean => column.visible)
	})
	protected resultRows: Signal<RowInterface<Item | null>[]> = computed((): RowInterface<Item | null>[] => {
		const rowsByItems: RowInterface<Item>[] = this.resultItems().map(
			(item: Item, index: number): RowInterface<Item> => ({
				index,
				item,
			}),
		)

		const dynamicRows: TableDynamicRowComponent[] = this.sourceDynamicRowsComponents.toArray()
		if (!dynamicRows.length) {
			return rowsByItems.map((row: RowInterface<Item>, index: number): RowInterface<Item> => ({ ...row, index }))
		}

		let resultRows: RowInterface<Item | null>[] = [...rowsByItems]

		dynamicRows.forEach((dynamicRowComponent: TableDynamicRowComponent): void => {
			const dynamicRowSettings: RowInterface<null> | null = dynamicRowComponent.rowSettings()
			if (!dynamicRowSettings) return

			const itemsBeforeDynamicRow: RowInterface<Item | null>[] = resultRows.slice(0, dynamicRowSettings.index)
			const itemsAfterDynamicRow: RowInterface<Item | null>[] = resultRows.slice(dynamicRowSettings.index)
			resultRows = [...itemsBeforeDynamicRow, dynamicRowSettings, ...itemsAfterDynamicRow]
		})

		return resultRows.map(
			(row: RowInterface<Item | null>, index: number): RowInterface<Item | null> => ({
				...row,
				index,
			}),
		)
	})

	protected isLoadingRows: WritableSignal<boolean> = signal<boolean>(false)

	protected isCompletedLoading: Signal<boolean> = computed<boolean>((): boolean => {
		return !this.isLoading() && !this.isLoadingRows()
	})

	private _sortByDefault(): void {
		const resultTableSettings: TableSettingsInterface | null = this.resultTableSettings()
		if (!resultTableSettings) return

		const prop: keyof Item | undefined = resultTableSettings?.defaultSort?.prop as keyof Item
		const type: SortDirType | undefined = resultTableSettings?.defaultSort?.type
		if (!prop && !type) return

		this.propForSort.set(prop)
		this._activeSortDir = type ?? 'asc'
		this.toSort(prop, false)
	}

	// Данный метод служит для высчитывания всей возможной высоты таблицы, при ресайзе виджета, таблица не занимала всю высоту
	private _observeContainerResize(): void {
		const tableScrollbar: HTMLElement | undefined = this.tableScrollbar?.nativeElement
		if (!tableScrollbar) return

		const containerEl: HTMLDivElement | null = tableScrollbar.closest('.content-scroll')
		if (!containerEl) return

		this._resizeObserver?.disconnect()
		this._resizeObserver = new ResizeObserver((): void => {
			const rect: DOMRect = containerEl.getBoundingClientRect()
			const bodyWidth: number = this.math.ceil(rect.width)
			const bodyHeight: number = this.math.ceil(rect.height) - 30

			this.bodyWidth.set(bodyWidth)
			this.bodyHeight.set(bodyHeight)
			this.actionsPosition.set(bodyWidth + tableScrollbar.scrollLeft)
		})

		this._resizeObserver.observe(containerEl)
	}

	private _listenScrollTable(): void {
		const tableScrollbar: HTMLElement | undefined = this.tableScrollbar?.nativeElement
		if (!tableScrollbar) return

		fromEvent(tableScrollbar, 'scroll')
			.pipe(takeUntil(this._destroy$))
			.subscribe((): void => {
				if (this.isLoadingTop() || this.isLoadingBottom() || this.isLoading()) return

				const isReachedTop: boolean = tableScrollbar.scrollTop === 0
				const isReachedBottom: boolean =
					tableScrollbar.scrollTop + tableScrollbar.clientHeight >= tableScrollbar.scrollHeight - 1

				if (isReachedTop) this.onReachTop.emit()
				if (isReachedBottom) this.onReachBottom.emit()

				this.actionsPosition.set(this.bodyWidth() + tableScrollbar.scrollLeft)
			})
	}

	private _initScrollListener(): void {
		if (this._isScrollListening) return

		this._listenScrollTable()
		this._isScrollListening = true
	}

	private _scrollToPositionInTable(): void {
		const tableScrollbar: HTMLElement | undefined = this.tableScrollbar?.nativeElement
		if (!tableScrollbar) return

		const scrollTop: number = this.scrollToPosition() - tableScrollbar.clientHeight / 2

		setTimeout((): void => {
			tableScrollbar.scrollTo({
				top: scrollTop,
				behavior: 'auto',
			})
		}, 0)
	}

	protected getIconSort(propSort: keyof Item | null): string {
		if (this._activeSortDir === 'asc' && this.propForSort() === propSort) {
			return '@tui.chevron-up'
		} else if (this._activeSortDir === 'desc' && this.propForSort() === propSort) {
			return '@tui.chevron-down'
		} else {
			return '@tui.chevrons-up-down'
		}
	}

	protected toSort<Key extends keyof Item>(propForSort: Key | null, isChangeDir: boolean = true): void {
		if (!propForSort) {
			this.resultItems.set([...this.items()])
			return
		}

		if (isChangeDir) {
			if (propForSort === this.propForSort()) {
				this._activeSortDir = this._activeSortDir === 'asc' ? 'desc' : 'asc'
			} else {
				this._activeSortDir = 'asc'
			}
		}

		if (propForSort !== this.propForSort()) {
			this.onChangeSort.emit({ prop: propForSort, type: this._activeSortDir })
		}

		const direction: SortDirType = this._activeSortDir
		this.propForSort.set(propForSort)

		const sortedItems: Item[] = [...this.items()].sort((a: Item, b: Item): number => {
			const aValue: Item[Key] = a[propForSort]
			const bValue: Item[Key] = b[propForSort]

			if (aValue == null && bValue == null) return 0
			if (aValue == null) return 1
			if (bValue == null) return -1

			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return direction === 'asc' ? aValue - bValue : bValue - aValue
			}

			if (!isNaN(+aValue) && !isNaN(+bValue)) {
				const numA: number = +aValue
				const numB: number = +bValue
				return direction === 'asc' ? numA - numB : numB - numA
			}

			const strA: string = aValue.toString().toLowerCase()
			const strB: string = bValue.toString().toLowerCase()

			return direction === 'asc' ? strA.localeCompare(strB) : strB.localeCompare(strA)
		})

		this.resultItems.set(sortedItems)
	}

	protected checkIsVisibleColumn(column: ColumnSettingsInterface<Item, Column>): boolean {
		const statesColumns: ColumnInterface<unknown>[] = this.statesColumns()
		const col: ColumnInterface<unknown> | undefined = statesColumns.find(
			(col: ColumnInterface<unknown>): boolean => col.column === column.column,
		)
		return !!col?.visible
	}

	protected setHoverRow(index: number | null): void {
		this.hoveredRowIndex.set(index)
	}

	protected onClickRow(item: Item): void {
		this.rowClick.emit(item)
	}

	protected searchDynamicRowComponent(row: RowInterface<Item | null>): TableDynamicRowComponent | null {
		return (
			this.sourceDynamicRowsComponents
				.toArray()
				.find((source: TableDynamicRowComponent): boolean => source.rowSettings()?.id === row.id) ?? null
		)
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['items']) {
			if (this._hasRenderedItems) {
				this.isFirstLoad.set(false)
			}

			this.isLoadingRows.set(true)
			this.toSort(null)
			this._observeContainerResize()

			if (changes['items'].isFirstChange()) {
				this._sortByDefault()
			} else {
				this.toSort(this.propForSort(), false)
				this._initScrollListener()
			}

			this.isLoadingRows.set(false)

			if (this.items().length > 0) {
				this._hasRenderedItems = true
			}
		}

		if (changes['isPoolScroll']) {
			this._scrollToPositionInTable()
		}
	}

	ngOnDestroy(): void {
		this._resizeObserver?.disconnect()
		this._destroy$.next()
		this._destroy$.complete()
	}
}
